import { VehicleAttributes } from '@/types'
import { UpdateVehicleMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditVehicleQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { ErrorMessage, Field, Label } from '../base/fieldset'
import { Selector as ManufacturerSelector } from './manufacturer/selector'

type VehicleType = EditVehicleQuery['vehicles']['nodes'][0]

interface Props {
  id: string
}

export const Form = ({ id }: Props) => {
  const {
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading,
    attributes,
    setAttributes,
    fieldErrors
  } = useForm<UpdateVehicleMutation, VehicleAttributes, EditVehicleQuery, VehicleType>({
    id,
    initializer: v => ({
      cabSize: v.cabSize || 0,
      registration: v.registration || '',
      warehouseId: v.warehouse?.id || '',
      rateCents: v.rateCents || 0,
      costCents: v.costCents || 0,
      mileageCents: v.mileageCents || 0,
      model: v.model || '',
      manufacturer: v.manufacturer || '',
      width: v.width || 0,
      height: v.height || 0,
      length: v.length || 0,
      payload: v.payload || 0,
      notes: v.notes || ''
    }),
    query: EditQuery,
    queryKey: 'vehicles',
    mutation: UpdateMutation,
    resultKey: 'updateVehicle'
  })

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        <div className="grid grid-cols-4 gap-6">
          <Field>
            <Label>Manufacturer</Label>
            <ManufacturerSelector
              invalid={!!fieldErrors?.manufacturer}
              value={attributes.manufacturer}
              onChange={value => {
                setAttributes({ ...attributes, manufacturer: value?.id || '' })
              }}
            />
            { fieldErrors?.manufacturer && <ErrorMessage>{ fieldErrors?.manufacturer }</ErrorMessage> }
          </Field>
          { formBuilder.buildField({ name: 'Model', field: 'model', type: 'text' }) }
          { formBuilder.buildField({ name: 'Registration', field: 'registration', type: 'text' }) }
          { formBuilder.buildField({ name: 'Warehouse', field: 'warehouseId', type: 'warehouse' }) }
        </div>
        <div className="grid grid-cols-4 gap-6">
          { formBuilder.buildField({ name: 'Cab size', field: 'cabSize', type: 'integer' }) }
          { formBuilder.buildField({ name: 'Cost', field: 'costCents', type: 'currency' }) }
          { formBuilder.buildField({ name: 'Hire rate', field: 'rateCents', type: 'currency' }) }
          { formBuilder.buildField({ name: 'Mileage rate', field: 'mileageCents', type: 'currency' }) }
        </div>
        <div className="grid grid-cols-4 gap-6">
          { formBuilder.buildField({ name: 'Payload', field: 'payload', type: 'weight' }) }
          { formBuilder.buildField({ name: 'Width', field: 'width', type: 'integer' }) }
          { formBuilder.buildField({ name: 'Height', field: 'height', type: 'integer' }) }
          { formBuilder.buildField({ name: 'Length', field: 'length', type: 'integer' }) }
        </div>
        { formBuilder.buildField({ name: 'Notes', field: 'notes', type: 'textarea' }) }

        { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
      </div>
    </>
  )
}
